import { FiltersTypes } from "@common/types/filters/general";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import {
    PaginatedTransactionsDataContext,
    PaginatedTransactionsActionsContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import {
    ActiveFilter,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { useCallback, useContext, useMemo, useRef } from "react";
import { getLockedTaskFilterState } from "./taskTransactionFilters";

interface UseLockedTaskFiltersResult {
    /** Active filters set by the task creator — shown as disabled pills. */
    lockedActiveFilters: ActiveFilter[];
    /** Live filters with the locked keys stripped, for the interactive overlay. */
    displayFilters: FiltersTypes;
    /** Applies a user filter change while re-forcing the locked creator values. */
    handleFiltersChange: FiltersChangeFn;
}

/**
 * Shared controls for a transaction task's filter overlay. Locked
 * (creator-set) filters are stripped from the overlay and forced back on every
 * user change, keeping the two transaction task bodies in sync.
 */
export function useLockedTaskFilters(
    baseFilters: FiltersTypes,
): UseLockedTaskFiltersResult {
    const { filters } = useContext(PaginatedTransactionsDataContext);
    const { setCurrentFilters, setPage } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const { lockedActiveFilters, lockedFilterKeys, lockedFilterValues } =
        useMemo(() => getLockedTaskFilterState(baseFilters), [baseFilters]);

    // Locked (creator-set) filters render as separate disabled pills, so
    // strip their keys from the filters handed to the interactive overlay.
    // Also hide sticky/specific-task transactionIds — they stay in the live
    // query filters but should not appear as an active filter pill.
    const displayFilters = useMemo(() => {
        const lockedKeys = new Set<string>(lockedFilterKeys);
        return omitBy(
            filters,
            (_value, key) => lockedKeys.has(key) || key === "transactionIds",
        );
    }, [filters, lockedFilterKeys]);

    // Ref keeps handleFiltersChange stable across filter changes.
    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const currentFilters = currentFiltersRef.current;
            // Re-apply locked creator-set values so they can never be changed.
            // Preserve sticky transactionIds even when they aren't in
            // baseFilters/lockedFilterValues (sticky stores them only on the
            // live view.filters snapshot).
            const newFilters = omitBy(
                {
                    ...currentFilters,
                    ...value,
                    ...lockedFilterValues,
                    ...(currentFilters.transactionIds !== undefined
                        ? { transactionIds: currentFilters.transactionIds }
                        : {}),
                },
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, currentFilters)) {
                return;
            }

            setPage(1);
            setCurrentFilters(newFilters);
        },
        [lockedFilterValues, setPage, setCurrentFilters],
    );

    return { lockedActiveFilters, displayFilters, handleFiltersChange };
}
